/**
 * 员工管理 - 常量定义
 *
 * 职责：定义员工相关的常量映射和配置
 * 来源：backend/app/Models/Employee.php
 *
 * @module types/employee/constants
 * @see backend/app/Models/Employee.php - 静态属性（$genderMap, $maritalStatusMap 等）
 *
 * @注意事项
 * 1. 常量对象的键必须与枚举值匹配
 * 2. 常量对象的值是中文标签
 * 3. 保持与后端 Map 数组的同步
 *
 * @更新时机
 * - 后端新增枚举映射时
 * - 后端修改中文标签时
 * - 枚举定义变更时
 *
 * @验证方式
 * 1. 对照 Employee.php 中的静态属性
 * 2. 确保所有枚举值都有对应的中文标签
 * 3. 确保标签文本与后端完全一致
 *
 * @last-updated 2025-10-18
 * @last-verified 2025-10-18
 */

import { Gender, MaritalStatus, EmploymentStatus, EmployeeStatus } from './enums';

/**
 * 性别标签映射
 */
export const GENDER_LABELS: Record<Gender, string> = {
  [Gender.UNKNOWN]: '未知',
  [Gender.MALE]: '男',
  [Gender.FEMALE]: '女',
};

/**
 * 婚姻状况标签映射
 */
export const MARITAL_STATUS_LABELS: Record<MaritalStatus, string> = {
  [MaritalStatus.SINGLE]: '未婚',
  [MaritalStatus.MARRIED]: '已婚',
  [MaritalStatus.DIVORCED]: '离异',
  [MaritalStatus.WIDOWED]: '丧偶',
};

/**
 * 在职状态标签映射
 */
export const EMPLOYMENT_STATUS_LABELS: Record<EmploymentStatus, string> = {
  [EmploymentStatus.ACTIVE]: '在职',
  [EmploymentStatus.RESIGNED]: '离职',
  [EmploymentStatus.SUSPENDED]: '停职',
};

/**
 * 状态标签映射
 */
export const EMPLOYEE_STATUS_LABELS: Record<EmployeeStatus, string> = {
  [EmployeeStatus.DISABLED]: '禁用',
  [EmployeeStatus.ENABLED]: '启用',
};

/**
 * 性别选项（用于下拉选择）
 */
export const GENDER_OPTIONS = [
  { label: '未知', value: Gender.UNKNOWN },
  { label: '男', value: Gender.MALE },
  { label: '女', value: Gender.FEMALE },
];

/**
 * 婚姻状况选项（用于下拉选择）
 */
export const MARITAL_STATUS_OPTIONS = [
  { label: '未婚', value: MaritalStatus.SINGLE },
  { label: '已婚', value: MaritalStatus.MARRIED },
  { label: '离异', value: MaritalStatus.DIVORCED },
  { label: '丧偶', value: MaritalStatus.WIDOWED },
];

/**
 * 在职状态选项（用于下拉选择）
 */
export const EMPLOYMENT_STATUS_OPTIONS = [
  { label: '在职', value: EmploymentStatus.ACTIVE },
  { label: '离职', value: EmploymentStatus.RESIGNED },
  { label: '停职', value: EmploymentStatus.SUSPENDED },
];

/**
 * 状态选项（用于下拉选择）
 */
export const EMPLOYEE_STATUS_OPTIONS = [
  { label: '禁用', value: EmployeeStatus.DISABLED },
  { label: '启用', value: EmployeeStatus.ENABLED },
];

